/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable import/named */
import React, { useContext, useRef, useState, useLayoutEffect } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Brand from '../Brand/Brand';
import Navigation from '../Navigation/Navigation';
import User from '../User/User';
import { dashboardMenu, demoPages, superAdminPages } from '../../menu';
import ThemeContext from '../../contexts/themeContext';
import Card, { CardBody } from '../../components/bootstrap/Card';

import Hand from '../../assets/img/hand.png';
import HandWebp from '../../assets/img/hand.webp';
import Icon from '../../components/icon/Icon';
import Button from '../../components/bootstrap/Button';
import Tooltips from '../../components/bootstrap/Tooltips';
import useDarkMode from '../../hooks/useDarkMode';
import useAsideTouch from '../../hooks/useAsideTouch';
import { signOutReset } from '../../redux/login/login.actions';
import { selectCurrentUser } from '../../redux/login/login.selector';

const Aside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);

	const { asideStyle, touchStatus, hasTouchButton, asideWidthWithSpace, x } = useAsideTouch();

	const isModernDesign = process.env.REACT_APP_MODERN_DESGIN === 'true';

	const constraintsRef = useRef(null);

	const [doc, setDoc] = useState(false);

	const { t } = useTranslation(['translation', 'menu']);

	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);

	const getCurrentUserLoginDetails = useSelector(selectCurrentUser);

	const dispatch = useDispatch();

	const navigate = useNavigate();

	const logOutScreen = () => {
		dispatch(signOutReset());
		navigate(`${demoPages.login.path}`);
	};

	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	useLayoutEffect(() => {
		dashboardMenu.masterPages.subMenu.Product.isView =
			getCurrentUserLoginDetails.data[0].CompanyIsProductBased > 0;
		dashboardMenu.operationPages.subMenu.Disbursement.isView =
			getCurrentUserLoginDetails.data[0].UserTypeName === 'Admin';
	}, [getCurrentUserLoginDetails]);

	return (
		<>
			<motion.aside
				style={asideStyle}
				className={classNames(
					'aside',
					{ open: asideStatus },
					{
						'aside-touch-bar': hasTouchButton && isModernDesign,
						'aside-touch-bar-close': !touchStatus && hasTouchButton && isModernDesign,
						'aside-touch-bar-open': touchStatus && hasTouchButton && isModernDesign,
					},
				)}>
				<div className='aside-head'>
					<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
				</div>
				<div className='aside-body'>
					{getCurrentUserLoginDetails.data[0].UserType === '1' ? (
						<Navigation menu={superAdminPages} id='aside-super-admin-pages' />
					) : (
						<>
							{/* <NavigationLine /> */}
							<Navigation menu={dashboardMenu} id='aside-dashboard' />
						</>
					)}

					{asideStatus && doc && (
						<Card className='m-3 '>
							<CardBody className='pt-0'>
								<img
									srcSet={HandWebp}
									src={Hand}
									alt='Hand'
									width={130}
									height={130}
								/>
								<p
									className={classNames('h4', {
										'text-dark': !darkModeStatus,
										'text-light': darkModeStatus,
									})}>
									{t('Everything is ready!')}
								</p>
								<Button
									color='info'
									isLight
									className='w-100'
									onClick={() => setDoc(false)}>
									{t('Demo Pages')}
								</Button>
							</CardBody>
						</Card>
					)}
				</div>
				<div className='aside-foot'>
					<nav aria-label='aside-bottom-menu'>
						<div className='navigation'>
							<div
								role='presentation'
								className='navigation-item cursor-pointer'
								onClick={logOutScreen}>
								<span className='navigation-link navigation-link-pill'>
									<span className='navigation-link-info'>
										<Icon icon='Logout' className='navigation-icon' />
										<span className='navigation-text'>Logout</span>
									</span>
								</span>
							</div>
							{/* Dark Mode */}
							<div
								role='presentation'
								className='navigation-item cursor-pointer'
								onClick={() => setDarkModeStatus(!darkModeStatus)}>
								{/* <Popovers trigger='hover' desc='Dark / Light mode'> */}
								<span className='navigation-link navigation-link-pill'>
									<span className='navigation-link-info'>
										<Icon
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...styledBtn}
											className='navigation-icon'
											icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
											aria-label='Toggle fullscreen'
											data-tour='dark-mode'
										/>
										<span className='navigation-text'>DarkMode</span>
									</span>
								</span>
								{/* </Popovers> */}
							</div>

							{/*	Full Screen */}
							<div
								role='presentation'
								className='navigation-item cursor-pointer'
								onClick={() => setFullScreenStatus(!fullScreenStatus)}>
								{/* <Popovers trigger='hover' desc='Fullscreen'> */}
								<span className='navigation-link navigation-link-pill'>
									<span className='navigation-link-info'>
										<Icon
											// eslint-disable-next-line react/jsx-props-no-spreading
											{...styledBtn}
											className='navigation-icon'
											icon={
												fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'
											}
											aria-label='Toggle dark mode'
										/>
										<span className='navigation-text'>Full Screen</span>
									</span>
								</span>
								{/* </Popovers> */}
							</div>
						</div>
					</nav>
					<User />
				</div>
			</motion.aside>
			{asideStatus && hasTouchButton && isModernDesign && (
				<>
					<motion.div className='aside-drag-area' ref={constraintsRef} />
					<Tooltips title='Toggle Aside' flip={['top', 'right']}>
						<motion.div
							className='aside-touch'
							drag='x'
							whileDrag={{ scale: 1.2 }}
							whileHover={{ scale: 1.1 }}
							dragConstraints={constraintsRef}
							// onDrag={(event, info) => console.log(info.point.x, info.point.y)}
							dragElastic={0.1}
							style={{ x, zIndex: 1039 }}
							onClick={() => x.set(x.get() === 0 ? asideWidthWithSpace : 0)}
						/>
					</Tooltips>
				</>
			)}
		</>
	);
};

export default Aside;
